import { createSlice } from '@reduxjs/toolkit';
import { fetchTestimonialId, fetchTestimonials } from './operations';

const initialState = {
  testimonialList: [
    {
      id: 4,
      title: 'Love Across the Pixels',
      description:
        'A heartfelt tale of two strangers, Alex and Mia, who found each other on an online dating site and turned a virtual connection into a real-life romance.',
      keywords:
        'love, online dating, romance, testimonials, Alex, Mia, connection, dating story',
      text: `
    Alex, a tech-savvy programmer with a penchant for adventure, had almost given up on finding a meaningful connection. Mia, a passionate writer with dreams of exploring the world, was hesitant to try online dating but decided to give it one last shot. "\n\n\n

    They met on a platform designed for meaningful connections. Their first interaction was simple—a shared interest in travel led to a conversation about their dream destinations. Soon, daily chats became the highlight of their days. 

    "Alex was so different," Mia recalls in her testimonial. "He didn’t just ask the usual questions. He wanted to know who I was at my core."

    Alex shares his side: "Mia had this warmth and wit that immediately drew me in. I knew early on that she was someone special."

    After months of chatting, they decided to meet in person. Their first date was at a cozy café in Mia’s city. Alex traveled hours just to see her. “When I saw Mia for the first time,” Alex says, “it felt like meeting an old friend, yet the excitement of something new was overwhelming.”

    Mia laughs, “He was everything I hoped he’d be, and more. He brought me my favorite book as a gift—it showed me how much he paid attention.”

    Fast forward a year, Alex and Mia are now planning their wedding. "We owe everything to taking that leap online," they say. Their story is a testament to how technology, when used genuinely, can bring hearts together.

    Testimonials:
    - Alex: "I never imagined I’d find love in a profile, but Mia changed my life. She’s my adventure, my home, and my best friend."
    - Mia: "I was skeptical about online dating, but Alex proved to me that sometimes, you just have to take a chance. He’s my forever."
  `,
    },

    {
      id: 3,
      title: 'A Swipe to Forever',
      description:
        'Discover the story of Emma and Liam, who found true love on an online dating app and proved that lasting connections can start with a simple swipe.',
      keywords:
        'online dating, love story, Emma, Liam, romance, connection, relationship success',
      text: `
    Emma, a graphic designer from New York City, had always been skeptical about dating apps. “It felt superficial,” she admits, “but my friends encouraged me to try.” Across the country in Portland, Liam, a wildlife photographer, had also downloaded the app after a friend insisted he give it a chance.

    Their profiles couldn’t have been more different. Emma’s was polished and creative, featuring her artwork and quirky captions. Liam’s showcased stunning photos of the wilderness and a bio that read, “Just a guy who loves the outdoors and his dog, Bailey.”

    “I swiped on Liam because he seemed real,” Emma says. “His photos weren’t staged, and his love for his dog won me over.” Liam recalls, “Emma’s energy jumped out at me. She seemed so passionate and full of life.”

    After matching, their conversation was effortless. They bonded over their shared love for nature, dogs, and photography. “We even joked about how opposites attract because she’s a city girl, and I’m all about the mountains,” Liam says.

    For weeks, they exchanged messages, video-called, and shared their lives virtually. When they finally met in person, they decided to meet halfway—in Chicago. “When I saw Emma walking towards me in the airport, I felt this rush of certainty,” Liam shares.

    Their first date was a mix of city adventures and quiet moments by the lake. “It was perfect,” Emma says. “Liam made me feel like I’d known him forever.”

    A year later, Emma and Liam have moved in together, blending their city-and-nature lifestyles. Emma works remotely now, traveling with Liam on his photography trips. “It’s funny,” Liam says. “I never thought a dating app would lead me to my soulmate.”

    Testimonials:
    - Emma: "I learned that love doesn’t follow rules or expectations. Meeting Liam through an app was unexpected but absolutely magical."
    - Liam: "Sometimes, the best connections come from places you least expect. Emma is my greatest adventure."
  `,
    },
    {
      id: 2,
      title: 'From DMs to "I Do"',
      description:
        'A touching story of Sarah and Ethan, two strangers brought together by an online dating platform who turned a virtual match into a lifelong commitment.',
      keywords:
        'online dating, Sarah, Ethan, love story, romance, digital connection, happy ending',
      text: `
    Sarah was a schoolteacher in a small town, juggling her love for teaching with her dream of finding someone to share her life with. Ethan, a software engineer living in a bustling city, had been swiping through profiles with little luck until one day, he stumbled across Sarah’s.

    “Her profile was different,” Ethan recalls. “She had this genuine smile and talked about her love for teaching kids. It felt real.” For Sarah, Ethan’s profile caught her attention because of his simplicity. “He wasn’t trying to impress anyone. He was just himself, and that made me want to know more.”

    Their first conversation lasted hours. “We talked about everything,” Sarah says. “From our favorite childhood memories to what we wanted in the future.” Ethan adds, “It didn’t feel like talking to someone new—it felt like reconnecting with an old friend.”

    After two months of chatting and countless video calls, Ethan made the drive to Sarah’s town for their first in-person date. They spent the day exploring her favorite local spots, from a quaint café to the town’s scenic park. “By the end of the day, I knew she was the one,” Ethan says.

    Their relationship grew quickly. “Ethan showed me what it meant to be loved and supported,” Sarah shares. A year later, Ethan proposed during a quiet picnic at the same park where they had their first date.

    Now married, Sarah and Ethan look back on their online dating journey with gratitude. “Without that platform, we might never have found each other,” Sarah says. “It’s proof that love can find you, even in the most unexpected ways.”

    Testimonials:
    - Sarah: "I didn’t think online dating could lead to something this real, but Ethan proved me wrong. He’s my partner, my biggest cheerleader, and my forever."
    - Ethan: "Sarah brought light into my life in a way I didn’t know I needed. I’m so thankful we took a chance on each other."
  `,
    },

    {
      id: 1,
      title: 'A Match Made in Messages',
      description:
        'Follow the story of Rachel and Noah, two busy professionals who found love on an online dating platform and turned their digital connection into a beautiful reality.',
      keywords:
        'online dating, Rachel, Noah, love story, romance, professional connection, happy ending',
      text: `
    Rachel, a marketing executive working in a bustling city, barely had time for a social life. “My work hours were crazy, and meeting people felt impossible,” she explains. Noah, an architect who spent most of his days on construction sites, felt the same. “I was always either at work or too tired to go out and meet anyone.”

    Both turned to online dating, not expecting much, but hoping for something different. When Rachel came across Noah’s profile, his love for design and his dream of building sustainable homes intrigued her. “He seemed passionate and grounded,” Rachel shares. For Noah, Rachel’s profile stood out because of her clever bio: “Marketing by day, dreamer by night.”

    Their first conversation started with Noah complimenting Rachel’s witty tagline. From there, they discovered a shared love for art, travel, and trying new cuisines. “We started talking about our bucket lists, and it just snowballed from there,” Noah says.

    After a few weeks of messaging, they decided to meet at an art exhibit in the city. “It was perfect,” Rachel recalls. “We spent hours walking through the gallery, talking about everything under the sun.”

    Over time, their relationship deepened. “Noah showed me what it means to have someone truly listen and care,” Rachel says. Noah adds, “Rachel brought so much joy into my life. She reminded me of the beauty in slowing down and appreciating the little things.”

    Two years later, Noah proposed to Rachel during a trip to an architectural landmark in Italy—combining their shared love for design and travel. Now planning their wedding, Rachel and Noah credit their online connection for bringing them together.

    Testimonials:
    - Rachel: "I never thought I’d meet someone so aligned with my dreams and values, especially online. Noah changed everything for me."
    - Noah: "Sometimes, life surprises you. Meeting Rachel felt like finding the missing piece I didn’t know I was looking for."
  `,
    },
  ],
  isLoading: false,
  error: null,
  testimonialIdDetail: {},
};

const handlePending = state => {
  state.isLoading = true;
  state.error = null;
};
const handleRejected = (state, { payload }) => {
  state.isLoading = false;
  state.error = payload;
};

export const sliceTestimonials = createSlice({
  name: 'Testimonials',
  initialState,
  reducers: {
    setCurrentTestimonialStory(state, { payload: userData }) {
      state.testimonialIdDetail = state.testimonialList.find(
        el => el.id == userData,
      );
    },
  },

  extraReducers: builder => {
    builder
      .addCase(fetchTestimonials.fulfilled, (state, { payload: userData }) => {
        state.isLoading = false;
        state.error = null;
        state.testimonialList = userData;
      })
      .addCase(fetchTestimonialId.fulfilled, (state, { payload: userData }) => {
        state.isLoading = false;
        state.error = null;
        state.testimonialIdDetail = userData;
      })
      .addCase(fetchTestimonials.pending, handlePending)
      .addCase(fetchTestimonialId.pending, handlePending)
      .addCase(fetchTestimonials.rejected, handleRejected)
      .addCase(fetchTestimonialId.rejected, handleRejected);
  },
});

export const { setCurrentTestimonialStory } = sliceTestimonials.actions;
export default sliceTestimonials.reducer;
